import {useEffect, useState} from 'react';
import {useQueryClient} from 'react-query';
import {useIntl} from 'react-intl';
import {
  Button as BootstrapButton,
  Card,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import ActionIcon from '../../../../Components/ActionIcon';
import Button from '../../../../Components/Button';
import Level3Title from '../../../../app/common/components/title/Level3Title';
import Loading from '../../../../app/common/components/Loading';
import {
  CompteMaterielIp,
  MaterielIp,
  StatutCheckMateriel,
  StatutMaterielIp,
  TypeModificationMateriel,
} from './TrunkSip/mocks';
import {
  TELEPHONES_IP_QUERY_KEY,
  useGetModelesMateriel,
  useGetSipsSrtpStatut,
  useGetTelephonesIp,
  useGetToIpFreeContacts,
  useGetToIpFreeNumbers,
  useSaveSipsSrtpStatut,
} from '../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../Hooks/useAutoDismissMessage';

/**
 * MAQUETTE — reproduction de « VoIP/ManageToIP.aspx » (« Gérer mes téléphones IP ») :
 * chiffrement SIPS+SRTP, 5 tableaux de matériel (associés / disponibles / multicomptes
 * IAD / non-autoprovisionnables / autres), association/dissociation/attribution de
 * contact, ajout de matériel. La programmation des touches BLF (ManageBLF.aspx) n'a
 * reçu qu'une analyse en survol côté legacy — le bouton « BLF » ouvre donc un simple
 * message d'indisponibilité plutôt qu'un écran inventé.
 *
 * Onglet réservé aux prestations Centrex (voir DetailsTelephonie.tsx) — pas affiché
 * pour une prestation Trunk SIP.
 */

const STATUT_TABLE_LABEL_ID: Record<StatutMaterielIp, string> = {
  associe: 'TELEPHONIE.TOIP.TABLE.ASSOCIES',
  disponible: 'TELEPHONIE.TOIP.TABLE.DISPONIBLES',
  multicompte: 'TELEPHONIE.TOIP.TABLE.MULTICOMPTES',
  manuel: 'TELEPHONIE.TOIP.TABLE.MANUELS',
  autre: 'TELEPHONIE.TOIP.TABLE.AUTRES',
};

const ORDRE_TABLES: StatutMaterielIp[] = ['associe', 'disponible', 'multicompte', 'manuel', 'autre'];

const identifiantMateriel = (materiel: MaterielIp): string => materiel.numeroSerie ?? materiel.mac ?? '—';

/**
 * 4 vérifications en parallèle (CheckHardware côté legacy), chacune avec 4 états possibles
 * (vert/orange/gris/rouge) — le sens exact de chaque check n'est pas détaillé dans l'analyse
 * reçue, ces 4 catégories et ce calcul sont donc une approximation raisonnable, pas une
 * reproduction confirmée.
 */
const STATUT_CHECK_ICON: Record<StatutCheckMateriel, string> = {
  ok: 'bi-check-circle-fill text-success',
  avertissement: 'bi-exclamation-triangle-fill text-warning',
  inconnu: 'bi-question-circle-fill text-secondary',
  erreur: 'bi-x-circle-fill text-danger',
};

const calculerStatutCheck = (materiel: MaterielIp, index: number): StatutCheckMateriel => {
  if (index === 3) {
    return materiel.statut === 'associe' || materiel.statut === 'multicompte' ? 'ok' : 'inconnu';
  }
  const cycle: StatutCheckMateriel[] = ['ok', 'ok', 'avertissement', 'ok', 'erreur', 'ok', 'inconnu', 'ok'];
  return cycle[(materiel.id + index) % cycle.length];
};

const CheckDiagnostic = ({materiel, onFermer}: {materiel: MaterielIp; onFermer: () => void}): JSX.Element => {
  const intl = useIntl();
  const LABELS_ID = [
    'TELEPHONIE.TOIP.CHECK.CONNECTIVITE',
    'TELEPHONIE.TOIP.CHECK.CONFIGURATION',
    'TELEPHONIE.TOIP.CHECK.FIRMWARE',
    'TELEPHONIE.TOIP.CHECK.ENREGISTREMENT_SIP',
  ];
  return (
    <Modal show onHide={onFermer} centered>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({id: 'TELEPHONIE.TOIP.CHECK.TITLE'})}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-around">
          {LABELS_ID.map((labelId, index) => {
            const statut = calculerStatutCheck(materiel, index);
            return (
              <OverlayTrigger key={labelId} overlay={<Tooltip>{intl.formatMessage({id: labelId})}</Tooltip>}>
                <div className="text-center">
                  <i className={`bi ${STATUT_CHECK_ICON[statut]} fs-1`} />
                  <div className="fs-8 text-muted">{intl.formatMessage({id: labelId})}</div>
                </div>
              </OverlayTrigger>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <BootstrapButton variant="secondary" onClick={onFermer}>
          {intl.formatMessage({id: 'COMMON.CLOSE'})}
        </BootstrapButton>
      </Modal.Footer>
    </Modal>
  );
};

interface FormulaireAssociationProps {
  materiel: MaterielIp;
  typeModification: TypeModificationMateriel;
  materielsDisponiblesPourDevice: MaterielIp[];
  onAnnuler: () => void;
  onEnregistrer: (comptes: CompteMaterielIp[], linkedIdMateriel?: number) => void;
  onChangerDevice: (nouvelIdMateriel: number) => void;
}

/**
 * Reproduit les 6 scénarios distincts de « ManageToIP.aspx » (#HidTypeModification) : associer/
 * associercsta/associeripiu partagent le même point d'entrée (AfficheAssocier) mais divergent
 * en aval — associercsta ajoute un select de device lié, associeripiu ajoute un combiné DECT
 * par ligne avec ses propres vérifications de doublon/cohérence ; attribuer se limite au choix
 * d'un contact (pas de numéro, pas d'ajout de ligne) ; updatedevice ne re-sélectionne pas le
 * numéro (déjà associé), seulement le device ; dissocier n'a pas besoin de formulaire (simple
 * confirmation, géré par la liste).
 */
const FormulaireAssociation = ({
  materiel,
  typeModification,
  materielsDisponiblesPourDevice,
  onAnnuler,
  onEnregistrer,
  onChangerDevice,
}: FormulaireAssociationProps): JSX.Element | null => {
  const intl = useIntl();
  const {data: numerosData, isLoading: isLoadingNumeros} = useGetToIpFreeNumbers();
  const {data: contactsData, isLoading: isLoadingContacts} = useGetToIpFreeContacts();
  const [slots, setSlots] = useState<CompteMaterielIp[]>(
    materiel.comptes.length > 0
      ? materiel.comptes
      : [{idWhitelist: 0, numero: '', numeroFormate: '', idContact: 0}]
  );
  const [nouvelIdMateriel, setNouvelIdMateriel] = useState<number | null>(null);
  const [deviceLieId, setDeviceLieId] = useState<number | null>(materiel.linkedIdMateriel ?? null);

  if (isLoadingNumeros || !numerosData || isLoadingContacts || !contactsData) {
    return <Loading className="col-md-6" />;
  }

  const numerosDisponibles = Array.from(
    new Set([...slots.map((s) => s.numeroFormate).filter(Boolean), ...numerosData])
  );

  if (typeModification === 'updatedevice') {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.TITLE.UPDATEDEVICE'})} />
          <Button
            label={intl.formatMessage({id: 'COMMON.BACK'})}
            faIcon="bi bi-arrow-left fs-6 me-2"
            onClick={onAnnuler}
          />
        </div>
        <p className="fs-6 mb-4">
          {intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.UPDATEDEVICE_DESCRIPTION'}, {softphone: `${materiel.marque} ${materiel.modele}`})}
        </p>
        {materielsDisponiblesPourDevice.length === 0 ? (
          <p className="text-muted fs-6">
            {intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.AUCUN_DEVICE_DISPONIBLE'})}
          </p>
        ) : (
          materielsDisponiblesPourDevice.map((candidat) => (
            <Form.Check
              key={candidat.id}
              type="radio"
              id={`device-${candidat.id}`}
              name="nouveau-device"
              className="mb-2"
              checked={nouvelIdMateriel === candidat.id}
              onChange={() => setNouvelIdMateriel(candidat.id)}
              label={
                <span>
                  <strong>
                    {candidat.marque} {candidat.modele}
                  </strong>{' '}
                  <span className="text-muted">({identifiantMateriel(candidat)})</span>
                </span>
              }
            />
          ))
        )}
        <div className="d-flex justify-content-center gap-3 mt-4">
          <BootstrapButton variant="secondary" onClick={onAnnuler}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
            disabled={nouvelIdMateriel == null}
            onClick={() => nouvelIdMateriel != null && onChangerDevice(nouvelIdMateriel)}
          />
        </div>
      </div>
    );
  }

  if (typeModification === 'attribuer') {
    const compte = materiel.comptes[0];
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.TITLE.ATTRIBUER'})} />
          <Button
            label={intl.formatMessage({id: 'COMMON.BACK'})}
            faIcon="bi bi-arrow-left fs-6 me-2"
            onClick={onAnnuler}
          />
        </div>
        <Card bg="light" className="mb-4">
          <Card.Body>
            <div className="fw-bold fs-6 mb-1">
              {materiel.marque} {materiel.modele}
            </div>
            <div className="text-muted fs-7">{compte?.numeroFormate}</div>
          </Card.Body>
        </Card>
        <Form.Label className="fw-bold fs-6">
          {intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.CONTACT'})}
        </Form.Label>
        <Form.Select
          style={{maxWidth: 320}}
          className="mb-4"
          value={slots[0]?.idContact ?? 0}
          onChange={(e) => {
            const idContact = Number(e.target.value);
            const contact = contactsData.find((c) => c.id === idContact);
            setSlots([{...slots[0], idContact, nomContact: contact?.nom}]);
          }}
        >
          <option value={0}>{intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.NON_ASSIGNE'})}</option>
          {contactsData.map((contact) => (
            <option key={contact.id} value={contact.id}>
              {contact.nom}
            </option>
          ))}
        </Form.Select>
        <div className="d-flex justify-content-center gap-3">
          <BootstrapButton variant="secondary" onClick={onAnnuler}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
            onClick={() => onEnregistrer(slots)}
          />
        </div>
      </div>
    );
  }

  const modifierSlot = (index: number, changement: Partial<CompteMaterielIp>): void => {
    const suivant = slots.map((slot, i) => (i === index ? {...slot, ...changement} : slot));
    const dernier = suivant[suivant.length - 1];
    const peutAjouter = suivant.length < materiel.nbCompteMax && dernier.numeroFormate !== '';
    setSlots(peutAjouter ? [...suivant, {idWhitelist: 0, numero: '', numeroFormate: '', idContact: 0}] : suivant);
  };

  const supprimerSlot = (index: number): void => {
    setSlots(slots.filter((_, i) => i !== index));
  };

  const estIpiu = typeModification === 'associeripiu';
  const estCsta = typeModification === 'associercsta';

  const slotsRemplis = slots.filter((slot) => slot.numeroFormate !== '' || (slot.combineIpiu ?? '') !== '');
  const numerosSaisis = slotsRemplis.map((slot) => slot.numeroFormate).filter(Boolean);
  const aDesDoublonsNumero = new Set(numerosSaisis).size !== numerosSaisis.length;

  const combinesSaisis = slotsRemplis.map((slot) => slot.combineIpiu).filter(Boolean) as string[];
  const aDesDoublonsCombine = estIpiu && new Set(combinesSaisis).size !== combinesSaisis.length;

  const aIncoherenceNumeroCombine =
    estIpiu && slotsRemplis.some((slot) => Boolean(slot.numeroFormate) !== Boolean(slot.combineIpiu));

  const auMoinsUnSlot = slotsRemplis.length > 0;
  const deviceRequisEtManquant = estCsta && deviceLieId == null;
  const peutEnregistrer =
    auMoinsUnSlot &&
    !aDesDoublonsNumero &&
    !aDesDoublonsCombine &&
    !aIncoherenceNumeroCombine &&
    !deviceRequisEtManquant;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Level3Title
          title={intl.formatMessage({
            id: `TELEPHONIE.TOIP.FORM.TITLE.${typeModification.toUpperCase()}`,
          })}
        />
        <Button
          label={intl.formatMessage({id: 'COMMON.BACK'})}
          faIcon="bi bi-arrow-left fs-6 me-2"
          onClick={onAnnuler}
        />
      </div>

      <Card bg="light" className="mb-4">
        <Card.Body>
          <div className="fw-bold fs-6 mb-1">
            {materiel.marque} {materiel.modele}
          </div>
          <div className="text-muted fs-7">{identifiantMateriel(materiel)}</div>
        </Card.Body>
      </Card>

      {aDesDoublonsNumero && (
        <div className="fw-bold text-danger mb-3">
          {intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.ERREUR_DOUBLON'})}
        </div>
      )}
      {aDesDoublonsCombine && (
        <div className="fw-bold text-danger mb-3">
          {intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.ERREUR_DOUBLON_COMBINE'})}
        </div>
      )}
      {aIncoherenceNumeroCombine && (
        <div className="fw-bold text-danger mb-3">
          {intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.ERREUR_COHERENCE_COMBINE'})}
        </div>
      )}

      {slots.map((slot, index) => (
        <Row key={index} className="g-3 align-items-end mb-3">
          <Col xs={12} md={estCsta ? 3 : estIpiu ? 3 : 4}>
            <Form.Label className="fw-bold fs-7">
              {intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.NUMERO'})}
            </Form.Label>
            <Form.Select
              value={slot.numeroFormate}
              onChange={(e) => modifierSlot(index, {numeroFormate: e.target.value, numero: e.target.value.replace(/\s/g, '')})}
            >
              <option value="">{intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.CHOISIR_NUMERO'})}</option>
              {numerosDisponibles.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={estCsta ? 3 : estIpiu ? 3 : 4}>
            <Form.Label className="fw-bold fs-7">
              {intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.CONTACT'})}
            </Form.Label>
            <Form.Select
              value={slot.idContact}
              onChange={(e) => {
                const idContact = Number(e.target.value);
                const contact = contactsData.find((c) => c.id === idContact);
                modifierSlot(index, {idContact, nomContact: contact?.nom});
              }}
            >
              <option value={0}>{intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.NON_ASSIGNE'})}</option>
              {contactsData.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.nom}
                </option>
              ))}
            </Form.Select>
          </Col>
          {estIpiu && (
            <Col xs={12} md={3}>
              <Form.Label className="fw-bold fs-7">
                {intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.COMBINE'})}
              </Form.Label>
              <Form.Control
                value={slot.combineIpiu ?? ''}
                onChange={(e) => modifierSlot(index, {combineIpiu: e.target.value})}
                placeholder={intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.COMBINE_PLACEHOLDER'})}
              />
            </Col>
          )}
          {estCsta && (
            <Col xs={12} md={3}>
              <Form.Label className="fw-bold fs-7">
                {intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.DEVICE_LIE'})}
              </Form.Label>
              <Form.Select
                value={deviceLieId ?? ''}
                onChange={(e) => setDeviceLieId(e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">{intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.CHOISIR_DEVICE'})}</option>
                {materielsDisponiblesPourDevice.map((candidat) => (
                  <option key={candidat.id} value={candidat.id}>
                    {candidat.marque} {candidat.modele}
                  </option>
                ))}
              </Form.Select>
            </Col>
          )}
          {slots.length > 1 && (
            <Col xs={12} md={1} className="text-end">
              <i
                className="bi bi-trash text-danger cursor-pointer fs-4"
                onClick={() => supprimerSlot(index)}
              />
            </Col>
          )}
        </Row>
      ))}

      <div className="d-flex justify-content-center gap-3 mt-4">
        <BootstrapButton variant="secondary" onClick={onAnnuler}>
          {intl.formatMessage({id: 'CANCEL'})}
        </BootstrapButton>
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
          disabled={!peutEnregistrer}
          onClick={() => onEnregistrer(slotsRemplis, estCsta ? deviceLieId ?? undefined : undefined)}
        />
      </div>
    </div>
  );
};

interface FormulaireAjoutProps {
  onAnnuler: () => void;
  onEnregistrer: (ajout: {marque: string; modele: string; mac?: string; numeroSerie?: string}) => void;
}

const FormulaireAjout = ({onAnnuler, onEnregistrer}: FormulaireAjoutProps): JSX.Element | null => {
  const intl = useIntl();
  const {data: modeles, isLoading} = useGetModelesMateriel();
  const [idModele, setIdModele] = useState('');
  const [identifiant, setIdentifiant] = useState('');
  const [etape, setEtape] = useState<'saisie' | 'relecture'>('saisie');
  const [disclaimerAccepte, setDisclaimerAccepte] = useState(false);

  if (isLoading || !modeles) {
    return <Loading className="col-md-6" />;
  }

  const modeleChoisi = modeles.find((m) => String(m.id) === idModele);
  const estDect = modeleChoisi?.categorie === 'dect';

  if (etape === 'relecture' && modeleChoisi) {
    return (
      <div>
        <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.TOIP.AJOUT.TITLE_RELECTURE'})} />
        <Card bg="light" className="mb-4 mt-3">
          <Card.Body>
            <div className="fs-6 mb-1">
              <strong>{intl.formatMessage({id: 'TELEPHONIE.TOIP.AJOUT.MODELE'})}</strong> {modeleChoisi.marque}{' '}
              {modeleChoisi.modele}
            </div>
            <div className="fs-6">
              <strong>
                {intl.formatMessage({
                  id: estDect ? 'TELEPHONIE.TOIP.AJOUT.NUMERO_SERIE' : 'TELEPHONIE.TOIP.AJOUT.MAC',
                })}
              </strong>{' '}
              {identifiant}
            </div>
          </Card.Body>
        </Card>
        <Form.Check
          type="checkbox"
          id="disclaimer-ajout-materiel"
          className="mb-4"
          checked={disclaimerAccepte}
          onChange={(e) => setDisclaimerAccepte(e.target.checked)}
          label={intl.formatMessage({id: 'TELEPHONIE.TOIP.AJOUT.DISCLAIMER'})}
        />
        <div className="d-flex justify-content-center gap-3">
          <BootstrapButton variant="secondary" onClick={() => setEtape('saisie')}>
            {intl.formatMessage({id: 'COMMON.BACK'})}
          </BootstrapButton>
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
            disabled={!disclaimerAccepte}
            onClick={() =>
              onEnregistrer({
                marque: modeleChoisi.marque,
                modele: modeleChoisi.modele,
                mac: estDect ? undefined : identifiant,
                numeroSerie: estDect ? identifiant : undefined,
              })
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.TOIP.AJOUT.TITLE'})} />
        <Button
          label={intl.formatMessage({id: 'COMMON.BACK'})}
          faIcon="bi bi-arrow-left fs-6 me-2"
          onClick={onAnnuler}
        />
      </div>

      <Form.Label className="fw-bold fs-6">
        {intl.formatMessage({id: 'TELEPHONIE.TOIP.AJOUT.MODELE'})}
      </Form.Label>
      <Form.Select
        style={{maxWidth: 360}}
        className="mb-4"
        value={idModele}
        onChange={(e) => {
          setIdModele(e.target.value);
          setIdentifiant('');
        }}
      >
        <option value="">{intl.formatMessage({id: 'TELEPHONIE.TOIP.AJOUT.CHOISIR_MODELE'})}</option>
        {modeles.map((modele) => (
          <option key={modele.id} value={modele.id}>
            {modele.marque} {modele.modele}
          </option>
        ))}
      </Form.Select>

      {modeleChoisi && (
        <>
          <div className="text-muted fs-7 mb-3">
            {intl.formatMessage(
              {id: 'TELEPHONIE.TOIP.AJOUT.FIRMWARE_MINIMUM'},
              {version: modeleChoisi.firmwareMinimum}
            )}
          </div>
          <Form.Label className="fw-bold fs-6">
            {intl.formatMessage({
              id: estDect ? 'TELEPHONIE.TOIP.AJOUT.NUMERO_SERIE' : 'TELEPHONIE.TOIP.AJOUT.MAC',
            })}
          </Form.Label>
          <Form.Control
            style={{maxWidth: 320}}
            className="mb-4"
            value={identifiant}
            onChange={(e) => setIdentifiant(e.target.value)}
          />
        </>
      )}

      <div className="d-flex justify-content-center gap-3 mt-3">
        <BootstrapButton variant="secondary" onClick={onAnnuler}>
          {intl.formatMessage({id: 'CANCEL'})}
        </BootstrapButton>
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.TOIP.AJOUT.CONTINUER'})}
          disabled={!modeleChoisi || identifiant.trim() === ''}
          onClick={() => setEtape('relecture')}
        />
      </div>
    </div>
  );
};

const ChiffrementSipsSrtp = (): JSX.Element | null => {
  const intl = useIntl();
  const {data, isLoading} = useGetSipsSrtpStatut();
  const {mutate: enregistrer} = useSaveSipsSrtpStatut();
  const [actifEdite, setActifEdite] = useState(false);
  const [confirmationOuverte, setConfirmationOuverte] = useState(false);

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  if (!data.disponible) {
    return (
      <Card bg="light" className="mb-5">
        <Card.Body>
          <div className="fw-bold fs-6 mb-2 text-uppercase">
            <i className="bi bi-shield-lock-fill me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.TOIP.SIPSSRTP.TITLE'})}
          </div>
          <p className="fs-6 mb-0">
            {intl.formatMessage({id: 'TELEPHONIE.TOIP.SIPSSRTP.RESIDENTIEL'})}
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card bg="light" className="mb-5">
      <Card.Body>
        <div className="fw-bold fs-6 mb-2 text-uppercase">
          <i className="bi bi-shield-lock-fill me-2" />
          {intl.formatMessage({id: 'TELEPHONIE.TOIP.SIPSSRTP.TITLE'})}
        </div>
        <p className="fs-6 mb-3">{intl.formatMessage({id: 'TELEPHONIE.TOIP.SIPSSRTP.DESCRIPTION'})}</p>

        <Form.Check
          type="radio"
          id="sipssrtp-inactif"
          name="sipssrtp"
          className="mb-2"
          checked={!actifEdite}
          onChange={() => setActifEdite(false)}
          label={
            <span>
              <i className="bi bi-unlock-fill me-2" />
              {intl.formatMessage({id: 'TELEPHONIE.TOIP.SIPSSRTP.INACTIF'})}
            </span>
          }
        />
        <Form.Check
          type="radio"
          id="sipssrtp-actif"
          name="sipssrtp"
          className="mb-3"
          checked={actifEdite}
          onChange={() => setActifEdite(true)}
          label={
            <span>
              <i className="bi bi-lock-fill me-2" />
              {intl.formatMessage({id: 'TELEPHONIE.TOIP.SIPSSRTP.ACTIF'})}
            </span>
          }
        />

        <div className="d-flex gap-3">
          <BootstrapButton variant="secondary" onClick={() => setActifEdite(data.actif)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.TOIP.SIPSSRTP.APPLIQUER'})}
            disabled={actifEdite === data.actif}
            onClick={() => setConfirmationOuverte(true)}
          />
        </div>
      </Card.Body>

      <Modal show={confirmationOuverte} onHide={() => setConfirmationOuverte(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.TOIP.SIPSSRTP.CONFIRM_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage({
            id: actifEdite
              ? 'TELEPHONIE.TOIP.SIPSSRTP.CONFIRM_BODY_ACTIF'
              : 'TELEPHONIE.TOIP.SIPSSRTP.CONFIRM_BODY_INACTIF',
          })}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setConfirmationOuverte(false)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton
            variant="primary"
            onClick={() => {
              enregistrer(actifEdite);
              setConfirmationOuverte(false);
            }}
          >
            {intl.formatMessage({id: 'TELEPHONIE.TOIP.SIPSSRTP.APPLIQUER'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

const GererTelephonesIp = (): JSX.Element | null => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const {data, isLoading} = useGetTelephonesIp();
  const [materielsLocal, setMaterielsLocal] = useState<MaterielIp[] | null>(null);
  const [message, setMessage] = useAutoDismissMessage();

  const [vue, setVue] = useState<'liste' | 'associer' | 'ajouter'>('liste');
  const [materielCible, setMaterielCible] = useState<MaterielIp | null>(null);
  const [typeModification, setTypeModification] = useState<TypeModificationMateriel>('associer');

  const [materielASupprimer, setMaterielASupprimer] = useState<MaterielIp | null>(null);
  const [materielADissocier, setMaterielADissocier] = useState<MaterielIp | null>(null);
  const [materielACheck, setMaterielACheck] = useState<MaterielIp | null>(null);
  const [blfIndisponibleOuvert, setBlfIndisponibleOuvert] = useState(false);

  useEffect(() => {
    if (data) {
      setMaterielsLocal(data);
    }
  }, [data]);

  if (isLoading || !data || !materielsLocal) {
    return <Loading className="col-md-6" />;
  }

  const mettreAJour = (suivant: MaterielIp[]): void => {
    setMaterielsLocal(suivant);
    queryClient.setQueryData(TELEPHONES_IP_QUERY_KEY, suivant);
  };

  /** associer/associercsta/associeripiu partagent le même point d'entrée côté legacy (AfficheAssocier) ; le type exact ne dépend que de la nature du matériel. */
  const typeAssociationPourMateriel = (materiel: MaterielIp): TypeModificationMateriel =>
    materiel.isCsta ? 'associercsta' : materiel.isIpiu ? 'associeripiu' : 'associer';

  const ouvrirAssociation = (materiel: MaterielIp, type: TypeModificationMateriel): void => {
    setMaterielCible(materiel);
    setTypeModification(type);
    setVue('associer');
  };

  const enregistrerAssociation = (comptes: CompteMaterielIp[], linkedIdMateriel?: number): void => {
    if (!materielCible) {
      return;
    }
    const nouveauStatut: StatutMaterielIp = materielCible.isIad
      ? 'multicompte'
      : comptes.length > 0
        ? 'associe'
        : 'disponible';
    mettreAJour(
      materielsLocal.map((m) =>
        m.id === materielCible.id ? {...m, comptes, statut: nouveauStatut, linkedIdMateriel} : m
      )
    );
    setMessage(intl.formatMessage({id: 'TELEPHONIE.TOIP.SUCCESS_MESSAGE'}));
    setVue('liste');
    setMaterielCible(null);
  };

  const confirmerDissociation = (): void => {
    if (!materielADissocier) {
      return;
    }
    mettreAJour(
      materielsLocal.map((m) =>
        m.id === materielADissocier.id ? {...m, comptes: [], statut: 'disponible'} : m
      )
    );
    setMaterielADissocier(null);
  };

  const confirmerSuppression = (): void => {
    if (!materielASupprimer) {
      return;
    }
    mettreAJour(materielsLocal.filter((m) => m.id !== materielASupprimer.id));
    setMaterielASupprimer(null);
  };

  const activerMateriel = (materiel: MaterielIp): void => {
    mettreAJour(materielsLocal.map((m) => (m.id === materiel.id ? {...m, statut: 'disponible'} : m)));
  };

  const changerDevice = (nouvelIdMateriel: number): void => {
    if (!materielCible) {
      return;
    }
    mettreAJour(
      materielsLocal.map((m) =>
        m.id === materielCible.id ? {...m, linkedIdMateriel: nouvelIdMateriel} : m
      )
    );
    setMessage(intl.formatMessage({id: 'TELEPHONIE.TOIP.SUCCESS_MESSAGE'}));
    setVue('liste');
    setMaterielCible(null);
  };

  const ajouterMateriel = (ajout: {marque: string; modele: string; mac?: string; numeroSerie?: string}): void => {
    const nouvelId = Math.max(0, ...materielsLocal.map((m) => m.id)) + 1;
    mettreAJour([
      ...materielsLocal,
      {
        id: nouvelId,
        marque: ajout.marque,
        modele: ajout.modele,
        mac: ajout.mac,
        numeroSerie: ajout.numeroSerie,
        prestationId: materielsLocal[0]?.prestationId ?? '',
        statut: 'disponible',
        isIad: false,
        isCsta: false,
        isSoftphone: false,
        isIpiu: ajout.numeroSerie != null,
        canDelete: true,
        canEdit: false,
        nbCompteMax: 1,
        comptes: [],
      },
    ]);
    setMessage(intl.formatMessage({id: 'TELEPHONIE.TOIP.SUCCESS_MESSAGE'}));
    setVue('liste');
  };

  if (vue === 'associer' && materielCible) {
    return (
      <FormulaireAssociation
        materiel={materielCible}
        typeModification={typeModification}
        materielsDisponiblesPourDevice={materielsLocal.filter(
          (m) => m.statut === 'disponible' && !m.isSoftphone && m.id !== materielCible.id
        )}
        onAnnuler={() => {
          setVue('liste');
          setMaterielCible(null);
        }}
        onEnregistrer={enregistrerAssociation}
        onChangerDevice={changerDevice}
      />
    );
  }

  if (vue === 'ajouter') {
    return (
      <FormulaireAjout onAnnuler={() => setVue('liste')} onEnregistrer={ajouterMateriel} />
    );
  }

  const materielsParStatut = (statut: StatutMaterielIp): MaterielIp[] =>
    materielsLocal.filter((m) => m.statut === statut);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.TOIP.TITLE'})} />
        <Button
          label={intl.formatMessage({id: 'TELEPHONIE.TOIP.AJOUTER'})}
          faIcon="bi bi-plus-lg fs-6 me-2"
          onClick={() => setVue('ajouter')}
        />
      </div>

      <ChiffrementSipsSrtp />

      {message && <div className="fw-bold text-success mb-4">{message}</div>}

      {ORDRE_TABLES.map((statut) => {
        const materiels = materielsParStatut(statut);
        if (materiels.length === 0) {
          return null;
        }
        return (
          <Card key={statut} className="mb-4">
            <Card.Body>
              <div className="fw-bold fs-6 mb-3 text-uppercase">
                {intl.formatMessage({id: STATUT_TABLE_LABEL_ID[statut]})}
              </div>
              <div className="tbl-wrap">
                <table className="table table-sm align-middle mb-0">
                  <thead>
                    <tr className="table-primary">
                      <th>{intl.formatMessage({id: 'TELEPHONIE.TOIP.TABLE.MATERIEL'})}</th>
                      {statut === 'associe' && (
                        <th>{intl.formatMessage({id: 'TELEPHONIE.TOIP.TABLE.NUMERO'})}</th>
                      )}
                      {statut === 'multicompte' && (
                        <th>{intl.formatMessage({id: 'TELEPHONIE.TOIP.TABLE.COMPTES'})}</th>
                      )}
                      <th className="text-end">
                        {intl.formatMessage({id: 'TELEPHONIE.TOIP.TABLE.ACTIONS'})}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {materiels.map((materiel) => (
                      <tr key={materiel.id}>
                        <td>
                          <div className="fw-semibold fs-7">
                            {materiel.marque} {materiel.modele}
                          </div>
                          <div className="text-muted fs-8">{identifiantMateriel(materiel)}</div>
                        </td>
                        {statut === 'associe' && (
                          <td>
                            {materiel.comptes.map((compte) => (
                              <div key={compte.idWhitelist} className="fs-7">
                                <strong>{compte.numeroFormate}</strong>{' '}
                                {compte.nomContact ? (
                                  compte.nomContact
                                ) : (
                                  <span
                                    className="text-primary cursor-pointer"
                                    onClick={() => ouvrirAssociation(materiel, 'attribuer')}
                                  >
                                    {intl.formatMessage({id: 'TELEPHONIE.TOIP.FORM.NON_ASSIGNE'})}
                                  </span>
                                )}
                                {compte.combineIpiu && ` — ${compte.combineIpiu}`}
                              </div>
                            ))}
                          </td>
                        )}
                        {statut === 'multicompte' && (
                          <td>
                            {intl.formatMessage(
                              {id: 'TELEPHONIE.TOIP.TABLE.COMPTES_COUNT'},
                              {actuel: materiel.comptes.length, max: materiel.nbCompteMax}
                            )}
                          </td>
                        )}
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-2">
                            {(statut === 'associe' || statut === 'multicompte') && !materiel.isCsta && (
                              <ActionIcon
                                faIcon="bi bi-diagram-3-fill"
                                label={intl.formatMessage({id: 'TELEPHONIE.TOIP.ACTION.BLF'})}
                                onClick={() => setBlfIndisponibleOuvert(true)}
                              />
                            )}
                            <ActionIcon
                              faIcon="bi bi-activity"
                              label={intl.formatMessage({id: 'TELEPHONIE.TOIP.ACTION.CHECK'})}
                              onClick={() => setMaterielACheck(materiel)}
                            />
                            {statut === 'disponible' && (
                              <ActionIcon
                                faIcon="bi bi-link-45deg"
                                label={intl.formatMessage({id: 'TELEPHONIE.TOIP.ACTION.ASSOCIER'})}
                                onClick={() =>
                                  ouvrirAssociation(materiel, typeAssociationPourMateriel(materiel))
                                }
                              />
                            )}
                            {(statut === 'associe' || statut === 'multicompte') && !materiel.isCsta && (
                              <ActionIcon
                                faIcon="bi bi-pencil-fill"
                                label={intl.formatMessage({id: 'TELEPHONIE.TOIP.ACTION.MODIFIER'})}
                                onClick={() =>
                                  ouvrirAssociation(materiel, typeAssociationPourMateriel(materiel))
                                }
                              />
                            )}
                            {materiel.isCsta && (
                              <ActionIcon
                                faIcon="bi bi-arrow-repeat"
                                label={intl.formatMessage({id: 'TELEPHONIE.TOIP.ACTION.UPDATEDEVICE'})}
                                onClick={() => ouvrirAssociation(materiel, 'updatedevice')}
                              />
                            )}
                            {statut === 'associe' && !materiel.isCsta && (
                              <ActionIcon
                                faIcon="bi bi-x-circle-fill"
                                label={intl.formatMessage({id: 'TELEPHONIE.TOIP.ACTION.DISSOCIER'})}
                                onClick={() => setMaterielADissocier(materiel)}
                              />
                            )}
                            {statut === 'manuel' && (
                              <Button
                                label={intl.formatMessage({id: 'TELEPHONIE.TOIP.ACTION.ACTIVER'})}
                                onClick={() => activerMateriel(materiel)}
                              />
                            )}
                            {materiel.canDelete && (
                              <ActionIcon
                                faIcon="bi bi-trash-fill"
                                label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
                                onClick={() => setMaterielASupprimer(materiel)}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        );
      })}

      <Modal show={materielADissocier != null} onHide={() => setMaterielADissocier(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{intl.formatMessage({id: 'TELEPHONIE.TOIP.DISSOCIER_BODY'})}</Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setMaterielADissocier(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerDissociation}>
            {intl.formatMessage({id: 'TELEPHONIE.TOIP.ACTION.DISSOCIER'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <Modal show={materielASupprimer != null} onHide={() => setMaterielASupprimer(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{intl.formatMessage({id: 'TELEPHONIE.TOIP.SUPPRIMER_BODY'})}</Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setMaterielASupprimer(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerSuppression}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <Modal show={blfIndisponibleOuvert} onHide={() => setBlfIndisponibleOuvert(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'TELEPHONIE.TOIP.ACTION.BLF'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{intl.formatMessage({id: 'TELEPHONIE.TOIP.BLF_INDISPONIBLE'})}</Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setBlfIndisponibleOuvert(false)}>
            {intl.formatMessage({id: 'COMMON.CLOSE'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      {materielACheck && (
        <CheckDiagnostic materiel={materielACheck} onFermer={() => setMaterielACheck(null)} />
      )}
    </div>
  );
};

export default GererTelephonesIp;
